// src/components/AdminDashboard/AdminDashboard.styles.js
import styled from 'styled-components';
import { Button, Typography, Table, TableCell, FormControl, Select, Paper } from '@mui/material';
import { media } from '../../../utils/media';

/* ═══════════════════════════════════════════════════════════════════════════════
   🎨 ADMIN DASHBOARD STYLES - ORGANIZED BY FUNCTIONALITY
   ═══════════════════════════════════════════════════════════════════════════════ */

/* ──────────────────────────────────────────────────────────────────────────────
   📦 MAIN CONTAINER & LAYOUT
────────────────────────────────────────────────────────────────────────────── */
export const Container = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primaryBackground} 0%, #f8f9fa 100%);
  padding: 40px;
  max-width: 1400px;
  margin: 40px auto;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentOrange} 100%);
  }

  ${media.tablet`
    padding: 30px 20px;
    margin: 20px;
    max-width: calc(100% - 40px);
  `}

  ${media.mobile`
    padding: 20px 15px;
    margin: 15px;
    max-width: calc(100% - 30px);
    border-radius: 16px;
  `}
`;

export const Title = styled(Typography)`
  color: ${({ theme }) => theme.colors.accentBlueGray};
  margin-bottom: 32px !important;
  text-align: center;
  font-weight: 700 !important;
  font-size: 2.5rem !important;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentBlueGray} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  ${media.mobile`
    font-size: 2rem !important;
    margin-bottom: 24px !important;
  `}
`;

/* ──────────────────────────────────────────────────────────────────────────────
   🧭 NAVIGATION & TABS
────────────────────────────────────────────────────────────────────────────── */
export const NavigationContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  overflow: hidden;
  border: 1px solid rgba(0, 166, 147, 0.1);
`;

export const TabGroupHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentBlueGray} 100%);
  color: white;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

/* ──────────────────────────────────────────────────────────────────────────────
   📋 SECTION CARDS & LAYOUT
────────────────────────────────────────────────────────────────────────────── */
export const SectionCard = styled(Paper)`
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 166, 147, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentOrange} 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    border-color: rgba(0, 166, 147, 0.2);

    &::before {
      opacity: 1;
    }
  }

  ${media.mobile`
    padding: 24px 20px;
    margin-bottom: 24px;
    border-radius: 12px;
  `}
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid rgba(0, 166, 147, 0.1);

  .MuiTypography-h6 {
    color: ${({ theme }) => theme.colors.accentBlueGray};
    font-weight: 700;
    font-size: 1.4rem;
    margin: 0;
  }

  ${media.mobile`
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding-bottom: 12px;
  `}
`;

export const SectionGroup = styled.div`
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const GroupTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.accentBlueGray};
  font-weight: 700;
  font-size: 1.2rem;
  margin-bottom: 16px;
  padding-left: 16px;
  border-left: 4px solid ${({ theme }) => theme.colors.accentTeal};
`;

/* ──────────────────────────────────────────────────────────────────────────────
   📊 TABLES & DATA DISPLAY
────────────────────────────────────────────────────────────────────────────── */
export const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  background: white;
`;

export const StyledTable = styled(Table)`
  min-width: 700px;
  
  .MuiTableHead-root {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentBlueGray} 100%);
    
    .MuiTableCell-head {
      color: white;
      font-weight: 700;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: none;
      padding: 16px;
    }
  }
  
  .MuiTableBody-root {
    .MuiTableRow-root {
      transition: background-color 0.2s ease;
      
      &:hover {
        background-color: rgba(0, 166, 147, 0.05);
      }
      
      &:nth-of-type(even) {
        background-color: rgba(0, 166, 147, 0.02);
      }
      
      .MuiTableCell-body {
        border-bottom: 1px solid rgba(90, 118, 132, 0.1);
        color: ${({ theme }) => theme.colors.accentBlueGray};
        font-weight: 500;
        padding: 16px;
        
        a {
          color: ${({ theme }) => theme.colors.accentTeal};
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s ease;
          
          &:hover {
            color: ${({ theme }) => theme.colors.accentOrange};
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

export const StyledTableCell = styled(TableCell)`
  color: ${({ theme }) => theme.colors.accentBlueGray};
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/* ──────────────────────────────────────────────────────────────────────────────
   📝 FORMS & INPUTS
────────────────────────────────────────────────────────────────────────────── */
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(0, 166, 147, 0.05) 0%, rgba(255, 111, 60, 0.05) 100%);
  border-radius: 12px;
  border: 1px solid rgba(0, 166, 147, 0.1);

  ${media.mobile`
    gap: 20px;
    padding: 20px;
  `}
`;

export const FormControlStyled = styled(FormControl)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    background-color: white;
    transition: all 0.3s ease;
    
    &:hover {
      border-color: ${({ theme }) => theme.colors.accentTeal};
    }
    
    &.Mui-focused {
      border-color: ${({ theme }) => theme.colors.accentTeal};
      box-shadow: 0 0 0 2px rgba(0, 166, 147, 0.2);
    }
  }
  
  .MuiInputLabel-root {
    color: ${({ theme }) => theme.colors.accentBlueGray};
    font-weight: 600;
    
    &.Mui-focused {
      color: ${({ theme }) => theme.colors.accentTeal};
    }
  }
`;

export const StyledSelect = styled(Select)`
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(90, 118, 132, 0.3);
    border-radius: 8px;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.accentTeal};
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.accentTeal};
    box-shadow: 0 0 0 2px rgba(0, 166, 147, 0.2);
  }
`;

/* ──────────────────────────────────────────────────────────────────────────────
   🔘 BUTTONS & ACTIONS
────────────────────────────────────────────────────────────────────────────── */
export const ResponsiveButton = styled(Button)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentBlueGray} 100%);
  color: white;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(0, 166, 147, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentBlueGray} 0%, ${({ theme }) => theme.colors.accentTeal} 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 166, 147, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }

  ${media.mobile`
    width: 100%;
    padding: 14px 24px;
  `}
`;

export const StyledLinkButton = styled(Button)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentBlueGray} 100%);
  color: white;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 2px 8px rgba(0, 166, 147, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentBlueGray} 0%, ${({ theme }) => theme.colors.accentTeal} 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 166, 147, 0.4);
  }
`;

export const StyledDeleteButton = styled(Button)`
  background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
  color: white;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #c53030 0%, #9c2626 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.4);
  }
`;

export const StyledSubmitButton = styled(Button)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.secondaryButton} 0%, ${({ theme }) => theme.colors.accentOrange} 100%);
  color: white;
  border-radius: 8px;
  padding: 14px 32px;
  font-weight: 700;
  text-transform: none;
  box-shadow: 0 4px 16px rgba(189, 32, 38, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentOrange} 0%, ${({ theme }) => theme.colors.secondaryButton} 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(189, 32, 38, 0.4);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #e0e0e0 0%, #d3d3d3 100%);
    color: #9e9e9e;
    transform: none;
    box-shadow: none;
  }

  ${media.mobile`
    width: 100%;
    padding: 16px 32px;
  `}
`;

export const ExportButton = styled(Button)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.secondaryButton} 0%, ${({ theme }) => theme.colors.accentOrange} 100%);
  color: white;
  margin-top: 16px;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(189, 32, 38, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.accentOrange} 0%, ${({ theme }) => theme.colors.secondaryButton} 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(189, 32, 38, 0.4);
  }

  svg {
    font-size: 1.2rem;
  }

  ${media.mobile`
    width: 100%;
    margin-top: 10px;
  `}
`;

export const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  ${media.mobile`
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  `}
`;

/* ──────────────────────────────────────────────────────────────────────────────
   📈 STATS & METRICS CARDS
────────────────────────────────────────────────────────────────────────────── */
export const StatsCard = styled(Paper)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 166, 147, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.accentTeal} 0%, ${({ theme }) => theme.colors.accentOrange} 100%);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  .MuiTypography-h6 {
    color: ${({ theme }) => theme.colors.accentTeal};
    font-weight: 700;
    font-size: 2rem;
    margin-bottom: 8px;
  }

  .MuiTypography-body2 {
    color: ${({ theme }) => theme.colors.accentBlueGray};
    font-weight: 500;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 32px;

  ${media.mobile`
    grid-template-columns: 1fr;
    gap: 16px;
  `}
`;

/* ──────────────────────────────────────────────────────────────────────────────
   💬 MESSAGES & FEEDBACK
────────────────────────────────────────────────────────────────────────────── */
export const NoDataMessage = styled(Typography)`
  text-align: center;
  color: ${({ theme }) => theme.colors.accentBlueGray};
  margin-top: 32px;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(0, 166, 147, 0.05) 0%, rgba(255, 111, 60, 0.05) 100%);
  border-radius: 12px;
  border: 2px dashed rgba(0, 166, 147, 0.3);
  font-size: 1.1rem;
  font-weight: 500;
`;

export const ErrorText = styled(Typography)`
  text-align: center;
  color: #e53e3e;
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(229, 62, 62, 0.1) 0%, rgba(197, 48, 48, 0.1) 100%);
  border-radius: 8px;
  border-left: 4px solid #e53e3e;
  font-weight: 600;
`;

export const SuccessMessage = styled(Typography)`
  text-align: center;
  color: #28a745;
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(34, 139, 58, 0.1) 100%);
  border-radius: 8px;
  border-left: 4px solid #28a745;
  font-weight: 600;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  .MuiCircularProgress-root {
    color: ${({ theme }) => theme.colors.accentTeal};
  }
`;

/* ──────────────────────────────────────────────────────────────────────────────
   📱 RESPONSIVE UTILITIES
────────────────────────────────────────────────────────────────────────────── */
export const MobileStack = styled.div`
  ${media.mobile`
    display: flex;
    flex-direction: column;
    gap: 16px;
  `}
`;

export const DesktopFlex = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  
  ${media.mobile`
    flex-direction: column;
    align-items: stretch;
  `}
`;

